import { IssueModel } from 'shared/domain/issue/issueModel';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { ReportFieldsContent } from '../types/model/common';
import { hasIssueFieldValueForPdfEstimate } from './hasIssueFieldValueForPdfEstimate';

/** Mirrors backend `SECTIONS` in app/interfaces/fields/section.ts */
export enum PdfEstimateSectionKey {
  metadata = 'metadata',
  attachments = 'attachments',
  linkedDocumentation = 'linkedDocumentation',
  baseInfo = 'baseInfo',
  technicalSpecifications = 'technicalSpecifications',
  time = 'time',
  involvedPersonnel = 'involvedPersonnel',
  correctiveAction = 'correctiveAction',
  issueAnalysis = 'issueAnalysis',
  fine = 'fine',
  costs = 'costs',
}

export const PDF_ESTIMATE_SECTIONS: Record<
  PdfEstimateSectionKey,
  IssueFieldNames[]
> = {
  [PdfEstimateSectionKey.metadata]: [
    IssueFieldNames.issueNumber,
    IssueFieldNames.stage,
    IssueFieldNames.hashtag,
  ],
  [PdfEstimateSectionKey.attachments]: [IssueFieldNames.documents],
  [PdfEstimateSectionKey.linkedDocumentation]: [IssueFieldNames.linkedDocumentation],
  [PdfEstimateSectionKey.baseInfo]: [
    IssueFieldNames.title,
    IssueFieldNames.site,
    IssueFieldNames.level,
    IssueFieldNames.positionOnMap,
    IssueFieldNames.subcontractors,
    IssueFieldNames.contractNumbers,
    IssueFieldNames.workTypes,
    IssueFieldNames.impact,
    IssueFieldNames.targetAmount,
    IssueFieldNames.completedAmount,
    IssueFieldNames.amountPercentage,
    IssueFieldNames.description,
    IssueFieldNames.hashtag,
  ],
  [PdfEstimateSectionKey.technicalSpecifications]: [
    IssueFieldNames.applicationNumber,
    IssueFieldNames.requirements,
    IssueFieldNames.productType,
    IssueFieldNames.producerDistributor,
    IssueFieldNames.typeModel,
    IssueFieldNames.installationLocation,
    IssueFieldNames.installationBasis,
    IssueFieldNames.constructionProductDocuments,
    IssueFieldNames.deviceDocuments,
    IssueFieldNames.additionalDocuments,
  ],
  [PdfEstimateSectionKey.time]: [
    IssueFieldNames.targetStartDate,
    IssueFieldNames.finalStartDate,
    IssueFieldNames.startDateDelay,
    IssueFieldNames.targetCompletionDate,
    IssueFieldNames.finalCompletionDate,
    IssueFieldNames.completionDateDelay,
    IssueFieldNames.detectionDate,
    IssueFieldNames.creationDate,
    IssueFieldNames.modificationDate,
    IssueFieldNames.targetResponseToAuthorDate,
    IssueFieldNames.finalResponseToAuthorDate,
    IssueFieldNames.responseToAuthorDelay,
    IssueFieldNames.delayReason,
    IssueFieldNames.sickLeaveStartDate,
    IssueFieldNames.sickLeaveEndDate,
  ],
  [PdfEstimateSectionKey.involvedPersonnel]: [
    IssueFieldNames.assignee,
    IssueFieldNames.numberOfEmployees,
    IssueFieldNames.executedByCompanies,
    IssueFieldNames.executor,
    IssueFieldNames.subcontractorRepresentative,
    IssueFieldNames.createdBy,
    IssueFieldNames.modifiedBy,
    IssueFieldNames.supervisor,
    IssueFieldNames.victim,
    IssueFieldNames.victimJobTitle,
    IssueFieldNames.employeeNumber,
    IssueFieldNames.employmentType,
  ],
  [PdfEstimateSectionKey.correctiveAction]: [
    IssueFieldNames.proposedCorrectiveAction,
    IssueFieldNames.solutionMethod,
    IssueFieldNames.solutionProposal,
    IssueFieldNames.immediateMeasures,
  ],
  [PdfEstimateSectionKey.issueAnalysis]: [
    IssueFieldNames.rootCauses,
    IssueFieldNames.effect,
    IssueFieldNames.hazardCategory,
    IssueFieldNames.personUnableToWork,
    IssueFieldNames.daysOfInabilityToWork,
    IssueFieldNames.circumstances,
    IssueFieldNames.issueVerification,
    IssueFieldNames.environmentalAspect,
    IssueFieldNames.spilledSubstance,
    IssueFieldNames.soilLeakageScale,
    IssueFieldNames.waterLeakageScale,
    IssueFieldNames.contaminatedSoilScale,
    IssueFieldNames.rejectionReason,
    IssueFieldNames.priority,
    IssueFieldNames.projectStage,
    IssueFieldNames.victimActivity,
    IssueFieldNames.injuryTypes,
    IssueFieldNames.injuredBodyParts,
  ],
  [PdfEstimateSectionKey.fine]: [
    IssueFieldNames.decisionToImposeFine,
    IssueFieldNames.expectedFine,
    IssueFieldNames.imposedFine,
  ],
  [PdfEstimateSectionKey.costs]: [
    IssueFieldNames.estimatedCost,
    IssueFieldNames.finalCost,
    IssueFieldNames.costCode,
  ],
};

/** Sections the issue report renders under their own headings, in their PDF order. */
export const PDF_SHARED_SECTION_ORDER: readonly PdfEstimateSectionKey[] = [
  PdfEstimateSectionKey.time,
  PdfEstimateSectionKey.involvedPersonnel,
  PdfEstimateSectionKey.correctiveAction,
  PdfEstimateSectionKey.issueAnalysis,
  PdfEstimateSectionKey.fine,
  PdfEstimateSectionKey.costs,
  PdfEstimateSectionKey.technicalSpecifications,
];

/** Fields always rendered in list/single key-info grid (backend base fields). */
export const PDF_KEY_INFO_BASE_FIELD_COUNT = 4;

/** Site and level are always rendered in key-info additional fields. */
export const PDF_KEY_INFO_FIXED_ADDITIONAL_FIELD_COUNT = 2;

export function isPdfFieldAvailableOnProcess(
  fieldName: IssueFieldNames,
  fieldsContent: ReportFieldsContent,
  selectedFields: Set<IssueFieldNames>,
  availableOnProcess: Set<IssueFieldNames>,
  hasValue: (fieldName: IssueFieldNames) => boolean
): boolean {
  if (!availableOnProcess.has(fieldName)) {
    return false;
  }

  switch (fieldsContent) {
    case ReportFieldsContent.all:
      return true;
    case ReportFieldsContent.filled:
      return hasValue(fieldName);
    case ReportFieldsContent.selected:
      return selectedFields.has(fieldName);
    default:
      return false;
  }
}

export function isPdfSectionAvailable(
  sectionKey: PdfEstimateSectionKey,
  isFieldAvailable: (fieldName: IssueFieldNames) => boolean
): boolean {
  return PDF_ESTIMATE_SECTIONS[sectionKey].some((fieldName) =>
    isFieldAvailable(fieldName)
  );
}

export type IssueFieldVisibilityOptions = {
  fieldsContent: ReportFieldsContent;
  selectedFieldNames: IssueFieldNames[];
  getAvailableFieldsForProcess: (processId: string) => Set<IssueFieldNames>;
};

/** Added by the backend to every selection of fields (`reportRequestParser`): the documents sections follow their own toggles. */
const ALWAYS_SELECTED_FIELDS: readonly IssueFieldNames[] = [
  IssueFieldNames.documents,
  IssueFieldNames.linkedDocumentation,
];

/**
 * Whether the PDF prints a field of the issue: enabled on its process and picked by
 * the field configuration. `hasValue` defaults to the issue's own data; a caller that
 * knows more (e.g. the counted attachments) passes its own.
 */
export function createIsIssueFieldVisible(
  issue: IssueModel,
  { fieldsContent, selectedFieldNames, getAvailableFieldsForProcess }: IssueFieldVisibilityOptions,
  hasValue: (fieldName: IssueFieldNames) => boolean = (name) => hasIssueFieldValueForPdfEstimate(issue, name)
): (fieldName: IssueFieldNames) => boolean {
  const availableOnProcess = getAvailableFieldsForProcess(issue.process);
  const selectedFields = new Set([...selectedFieldNames, ...ALWAYS_SELECTED_FIELDS]);
  return (fieldName: IssueFieldNames): boolean =>
    isPdfFieldAvailableOnProcess(fieldName, fieldsContent, selectedFields, availableOnProcess, hasValue);
}
